import { useEffect, useState } from 'react';

import type { IIncident } from '../../../interface/vesr';
import { type VesrConnectionStatus, VesrIncidentLiveClient } from '../../../lib/vesr-signalr';

/**
 * Panel-scoped live incident feed: connects to the VESR hub while the Reports
 * hub is open, joins the agent's state room (incident events broadcast to
 * `state_{stateCode}`), and accumulates the latest version of every incident
 * seen. Consumers overlay this map onto their fetched lists. Connection
 * failures degrade silently — polling/refresh stays the source of freshness.
 */
export const useIncidentLive = (stateCode?: string) => {
	const [liveIncidents, setLiveIncidents] = useState<Record<string, IIncident>>({});
	const [status, setStatus] = useState<VesrConnectionStatus>('disconnected');

	useEffect(() => {
		if (!stateCode) return;
		const client = new VesrIncidentLiveClient();
		const onIncident = (incident: IIncident) => {
			if (!incident?.id) return;
			setLiveIncidents((prev) => ({ ...prev, [incident.id]: incident }));
		};
		client.on('IncidentCreated', onIncident);
		client.on('IncidentUpdated', onIncident);
		void client.connect(setStatus).then(() => client.joinStateRoom(stateCode));
		return () => {
			void client.disconnect();
		};
	}, [stateCode]);

	return { liveIncidents, liveStatus: status };
};
